import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { CategoryId, Question, RoadSign } from '../types';
import { ROAD_SIGNS, SIGN_ISSUES, STATIC_BANK } from '../data/questionBank';
import type { BankIssue } from '../data/questionBank';
import { loadCustomQuestions, saveCustomQuestions } from '../services/customQuestions';

interface BankContextValue {
  /** Bank questions + locally added ones. */
  questions: Question[];
  signs: RoadSign[];
  issues: BankIssue[];
  customQuestions: Question[];
  byCategory: (id: CategoryId) => Question[];
  byId: (id: string) => Question | undefined;
  addCustom: (question: Question) => void;
  removeCustom: (id: string) => void;
}

const BankContext = createContext<BankContextValue | null>(null);

export function BankProvider({ children }: { children: ReactNode }) {
  const initial = useMemo(() => loadCustomQuestions(), []);
  const [custom, setCustom] = useState<Question[]>(initial.questions);

  const questions = useMemo(() => {
    const staticIds = new Set(STATIC_BANK.questions.map((q) => q.id));
    const extras = custom.filter((q) => !staticIds.has(q.id));
    return [...STATIC_BANK.questions, ...extras];
  }, [custom]);

  const questionMap = useMemo(() => {
    const map = new Map<string, Question>();
    for (const q of questions) map.set(q.id, q);
    return map;
  }, [questions]);

  const issues = useMemo<BankIssue[]>(
    () => [
      ...STATIC_BANK.issues,
      ...SIGN_ISSUES,
      ...initial.issues.map((message) => ({ origin: 'local storage', id: null, message })),
    ],
    [initial.issues],
  );

  const byCategory = useCallback(
    (id: CategoryId) => questions.filter((q) => q.category === id),
    [questions],
  );

  const byId = useCallback((id: string) => questionMap.get(id), [questionMap]);

  const addCustom = useCallback((question: Question) => {
    setCustom((prev) => {
      const next = [...prev.filter((q) => q.id !== question.id), { ...question, isCustom: true }];
      saveCustomQuestions(next);
      return next;
    });
  }, []);

  const removeCustom = useCallback((id: string) => {
    setCustom((prev) => {
      const next = prev.filter((q) => q.id !== id);
      saveCustomQuestions(next);
      return next;
    });
  }, []);

  const value = useMemo<BankContextValue>(
    () => ({
      questions,
      signs: ROAD_SIGNS,
      issues,
      customQuestions: custom,
      byCategory,
      byId,
      addCustom,
      removeCustom,
    }),
    [questions, issues, custom, byCategory, byId, addCustom, removeCustom],
  );

  return <BankContext.Provider value={value}>{children}</BankContext.Provider>;
}

export function useBank(): BankContextValue {
  const ctx = useContext(BankContext);
  if (!ctx) throw new Error('useBank must be used inside <BankProvider>');
  return ctx;
}
